import { Navigate, useLocation } from 'react-router';

/**
 * Rescue route for legacy PHP Hub URLs of the shape
 * `/hub/<module>/?p=<page>&id=…` — the pattern baked into old emails,
 * bookmarks and the live at-mysg.com hub. Without this they land on the
 * 404 page (the React router only knows `/<module>/…`).
 *
 * Mapping: drop the `/hub` prefix, promote `?p=<page>` into a path
 * segment, keep every other query param (deep-link `id`s ride along).
 * Page slugs that were renamed in the React port are translated via
 * `LEGACY_PAGE_SLUGS`; unknown slugs pass through untouched — worst
 * case they hit the module's own placeholder/404, which is still a
 * better landing than the top-level 404.
 */
const LEGACY_PAGE_SLUGS: Record<string, string> = {
  // hr: PHP `cash-claim.php` → React `/hr/cash-claims`
  'cash-claim': 'cash-claims',
};

export function legacyHubTarget(pathname: string, search: string): string {
  // `/hub/hr/` → `hr`; `/hub/` → '' → dashboard.
  const rest = pathname.replace(/^\/hub\/?/, '').replace(/\/+$/, '');
  if (!rest) return '/dashboard';
  // The PHP agent portal lived at hub/coportal/agentportal (CHANGELOG
  // 2026-05-11); its Amassa home is /agent, and its query means nothing there.
  if (/^coportal\/agentportal(\/|$)/.test(rest)) return '/agent';

  const params = new URLSearchParams(search);
  const p = params.get('p');
  params.delete('p');
  const page = p ? (LEGACY_PAGE_SLUGS[p] ?? p) : '';

  const path = page ? `/${rest}/${page}` : `/${rest}`;
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function LegacyHubRedirect() {
  const location = useLocation();
  return (
    <Navigate to={legacyHubTarget(location.pathname, location.search)} replace />
  );
}
